import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAutoSave } from "@/hooks/useAutoSave";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Upload, Image } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ImageLibraryPicker from "@/components/common/ImageLibraryPicker";
import { VoiceAgent } from "@/services/voiceAgentService";

const basicSettingsSchema = z.object({
  avatar_url: z.string().optional(),
  instructions: z.string().optional(),
});

type BasicSettingsFormValues = z.infer<typeof basicSettingsSchema>;

interface VoiceAgentBasicFormProps {
  agent: VoiceAgent;
  updateAgent: (data: Partial<VoiceAgent>) => Promise<boolean>;
  showOnly?: string[];
}

const VoiceAgentBasicForm = ({
  agent,
  updateAgent,
  showOnly,
}: VoiceAgentBasicFormProps) => {
  const [initialFormValues, setInitialFormValues] =
    useState<BasicSettingsFormValues | null>({
      avatar_url: agent.avatar_url || "",
      instructions: agent.instructions || "",
    });

  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const { toast } = useToast();

  const form = useForm<BasicSettingsFormValues>({
    resolver: zodResolver(basicSettingsSchema),
    defaultValues: {
      avatar_url: agent.avatar_url || "",
      instructions: agent.instructions || "",
    },
  });

  const { isSaving, setDirty } = useAutoSave({
    onSave: async () => {
      if (!initialFormValues) return;
      const data = form.getValues();
      return await updateAgent(data);
    },
    successMessage: "Voice agent settings saved automatically",
    errorMessage: "Failed to auto-save voice agent settings",
  });

  // Update form when agent data changes
  useEffect(() => {
    const newFormValues = {
      avatar_url: agent.avatar_url || "",
      instructions: agent.instructions || "",
    };
    
    form.reset(newFormValues);
    setInitialFormValues(newFormValues);
  }, [agent.id, agent.avatar_url, agent.instructions, form]);

  // Watch for changes
  useEffect(() => {
    if (!initialFormValues) return;
    const handleChange = () => {
      const data = form.getValues();
      const hasChanges = Object.keys(data).some((key) => {
        return (
          data[key as keyof BasicSettingsFormValues] !==
          initialFormValues[key as keyof BasicSettingsFormValues]
        );
      });
      if (hasChanges) {
        setDirty();
      }
    };
    const subscription = form.watch(handleChange);
    return () => subscription.unsubscribe();
  }, [form, setDirty, initialFormValues]);

  const shouldShowField = (fieldName: string) =>
    !showOnly || showOnly.includes(fieldName);

  // Get initial letter for avatar fallback
  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    const fileExt = file.name.split('.').pop();
    const allowedTypes = ['jpg', 'jpeg', 'png', 'gif'];
    if (!allowedTypes.includes(fileExt?.toLowerCase() || '')) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please upload a jpg, jpeg, png, or gif file.",
      });
      return;
    }

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: "Please upload a file smaller than 2MB.",
      });
      return;
    }

    setIsUploadingAvatar(true);

    try {
      // Generate a unique filename to avoid collisions
      const fileName = `voice-agent-${agent.id}-${Date.now()}.${fileExt}`;
      
      // Upload directly to the agent_files bucket
      const { data, error } = await supabase.storage
        .from('agent_files')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('agent_files')
        .getPublicUrl(fileName);

      // Update agent with new avatar URL
      const newAvatarUrl = publicUrlData.publicUrl;
      const updated = await updateAgent({ avatar_url: newAvatarUrl });

      if (updated) {
        form.setValue('avatar_url', newAvatarUrl);
        toast({
          title: "Avatar updated",
          description: "Your voice agent's avatar has been updated successfully.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Failed to update avatar",
          description: "Avatar was uploaded but failed to save to agent. Please try again.",
        });
      }
    } catch (error: any) {
      console.error("Storage error:", error);
      toast({
        variant: "destructive",
        title: "Failed to upload avatar",
        description: error.message || "An unexpected error occurred.",
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSelectFromLibrary = async (url: string) => {
    try {
      const updated = await updateAgent({ avatar_url: url });
      
      if (updated) {
        form.setValue('avatar_url', url);
        toast({
          title: "Avatar updated",
          description: "Your voice agent's avatar has been updated from the library.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Failed to update avatar",
          description: "Failed to update agent avatar. Please try again.",
        });
      }
    } catch (error: any) {
      console.error("Error updating avatar:", error);
      toast({
        variant: "destructive",
        title: "Failed to update avatar",
        description: error.message || "An unexpected error occurred.",
      });
    }
  };

  return (
    <Form {...form}>
      <form className="space-y-4">
        {/* Avatar */}
        {shouldShowField("avatar_url") && (
          <div className="flex items-center space-x-4 mb-6">
            <div className="relative">
              <Avatar className="h-20 w-20">
                {form.watch('avatar_url') ? (
                  <AvatarImage src={form.watch('avatar_url')} alt={agent.agent_name || agent.title} />
                ) : (
                  <AvatarFallback className="text-xl">
                    {getInitials(agent.agent_name || agent.title)}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="absolute -bottom-2 -right-2 flex space-x-1">
                <label 
                  htmlFor="avatar-upload" 
                  className="bg-primary text-primary-foreground p-1 rounded-full cursor-pointer hover:bg-primary/90 transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  <span className="sr-only">Upload avatar</span>
                </label>
                <input 
                  id="avatar-upload" 
                  type="file" 
                  className="hidden" 
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  disabled={isUploadingAvatar}
                />
                
                <ImageLibraryPicker
                  onSelectImage={handleSelectFromLibrary}
                  imageType="avatar"
                  selectedImage={form.watch('avatar_url') || undefined}
                  triggerButton={
                    <button type="button" className="bg-primary text-primary-foreground p-1 rounded-full cursor-pointer hover:bg-primary/90 transition-colors">
                      <Image className="h-4 w-4" />
                    </button>
                  }
                />
              </div>
            </div>
            <div>
              <h3 className="font-medium">Voice Agent Avatar</h3>
              <p className="text-sm text-muted-foreground">
                Upload an image or select from the library to represent your voice agent.
              </p>
              {isUploadingAvatar && (
                <div className="text-sm text-muted-foreground mt-1">
                  Uploading...
                </div>
              )}
            </div>
          </div>
        )}

        {/* Instructions */}
        {shouldShowField("instructions") && (
          <FormField
            control={form.control}
            name="instructions"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Instructions
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Provide detailed instructions for how the AI should behave..."
                    className="min-h-[120px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Auto-save indicator */}
        {isSaving && (
          <div className="flex justify-end">
            <span className="text-sm text-muted-foreground">Auto-saving…</span>
          </div>
        )}
      </form>
    </Form>
  );
};

export default VoiceAgentBasicForm; 